import { z } from 'zod'
import { InstantieId, RevisieId, SectionId } from './id.js'
import { Laag } from './laag.js'
import { Moment, compareMoments } from './moment.js'

/**
 * Een generatie van één sectiedocument (ontwerp 4.6, B6).
 *
 * Een sectie heeft niet één document dat eindeloos doorloopt maar een reeks
 * genummerde generaties. Dat is de begrenzing van de groei die volgt uit de keuze
 * om de opruiming van verwijderde inhoud uit te zetten: die keuze staat vast, want
 * ze is de voorwaarde onder de attributie en de verschilweergave, en de prijs is
 * een document dat monotoon groeit (bijlage C.4, valkuil 1). Die groei wordt
 * begrensd door de levensduur van het document en door niets anders.
 *
 * Deze vorm is wat de store-API onder `/generaties` teruggeeft (9.1): nummer,
 * moment, aanleiding, nulsnapshot, omvang en toestand. Het binaire document zelf
 * is een eigen resource; de opslag kent zijn formaat niet en interpreteert het
 * nooit.
 */

/** De vorm van een Yjs-snapshot in transport: base64, want JSON heeft geen bytes. */
export const Snapshot = z
  .string()
  .regex(/^[A-Za-z0-9+/]*={0,2}$/, 'verwacht base64')
export type Snapshot = z.infer<typeof Snapshot>

/**
 * Waarom deze generatie is begonnen.
 *
 * Drie aanleidingen sluiten er een af (4.6), en `uitvraag` is de eerste van de
 * eerste instantie. Alleen `compactie` is routine; `gedwongen-compactie` is de
 * uitzondering met een melding aan wie er verbonden is.
 */
export const Trigger = z.enum(['uitvraag', 'beurtwisseling', 'compactie', 'gedwongen-compactie'])
export type Trigger = z.infer<typeof Trigger>

/** Levend wordt beschreven, gearchiveerd nooit meer (4.6, 9.1). */
export const GeneratieStatus = z.enum(['levend', 'gearchiveerd'])
export type GeneratieStatus = z.infer<typeof GeneratieStatus>

export const Generatie = z
  .strictObject({
    instantieId: InstantieId,
    laag: Laag,
    sectieId: SectionId,
    nummer: z.number().int().positive(),
    aanleiding: Trigger,
    geopendOp: Moment,
    geslotenOp: Moment.nullable(),
    /** Het ijkpunt waartegen alles in deze generatie wordt afgezet (4.6, stap 2). */
    nulsnapshot: Snapshot,
    /** Bij een beurtwisseling is dit precies de snapshot die de revisie bewaart (4.5). */
    eindsnapshot: Snapshot.nullable(),
    omvangBytes: z.number().int().nonnegative(),
    toestand: GeneratieStatus,
    /** De revisie waar de reeks op rust. Null voor de eerste generatie van de eerste instantie. */
    revisieId: RevisieId.nullable(),
  })
  .superRefine((generatie, ctx) => {
    // Archiveren en sluiten zijn één handeling: de pod schrijft de stand uit, neemt
    // een eindsnapshot en archiveert (4.6). Een gearchiveerde generatie zonder
    // sluitmoment zou niet meer te plaatsen zijn in de tijd, en een levende met een
    // sluitmoment zou nog beschreven worden na zijn eind.
    if (generatie.toestand === 'gearchiveerd' && generatie.geslotenOp === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['geslotenOp'],
        message: 'een gearchiveerde generatie draagt zijn sluitmoment',
      })
    }
    if (generatie.toestand === 'levend' && generatie.geslotenOp !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['toestand'],
        message: 'een generatie met een sluitmoment is gearchiveerd',
      })
    }
    if (generatie.geslotenOp !== null && compareMoments(generatie.geslotenOp, generatie.geopendOp) < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['geslotenOp'],
        message: 'een generatie sluit niet voordat ze open was',
      })
    }
    if (generatie.eindsnapshot !== null && generatie.toestand !== 'gearchiveerd') {
      ctx.addIssue({
        code: 'custom',
        path: ['eindsnapshot'],
        message: 'een eindsnapshot hoort bij een gearchiveerde generatie',
      })
    }
  })
export type Generatie = z.infer<typeof Generatie>

/**
 * De schrijflease: het tweede van de drie sloten onder één schrijver per document
 * (ontwerp 3.2, B6).
 *
 * Elke `PUT` op een sectiedocument draagt hem, en de store weigert een
 * schrijfactie zonder geldige lease. Zo faalt een tweede schrijver luid in plaats
 * van in stilte een tweede werkelijkheid op te bouwen. De vervaltijd is kort
 * tegenover de vernieuwcadans, zodat een opvolger na een harde crash hooguit één
 * vervaltijd wacht; hij neemt een lease pas over nadat de oude is verlopen en
 * breekt hem nooit.
 */
export const WriteLease = z.strictObject({
  /** Welk podproces de lease houdt. Uit de omgeving van de pod, nooit uit invoer van een client. */
  houder: z.string().min(1),
  vervaltOp: Moment,
  /**
   * Hoe vaak deze generatie is overgenomen: de fencing-token tegen een late `writeDoc`- of
   * `refreshLease`-aanroep van vóór zo'n overname (3.2, slot 3). Verhoogt bij elke overname
   * (de lease was verlopen, of van een andere houder), niet bij een gewone verlenging.
   */
  epoch: z.number().int().positive(),
})
export type WriteLease = z.infer<typeof WriteLease>
